import {
    allowNextNavigation,
    api,
    ME_TOKEN,
    objectColorsSpec,
    type Prefetch,
    useAuthStore,
    useObjectColors,
    useZodForm,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    ColorTag,
    DateTime,
    Field,
    FormSheet,
    Input,
    type ListColumnDef,
    PageHeader,
    SearchableSelect,
    StatusBadge,
    statusLabel,
    StoreName,
    TagList,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import type { CaseRecord } from '@/api/types';
import { KindColorsDialog } from '@/components/object-colors';
import { TagFilter } from '@/components/tag-filter';
import {
    AdminListPage,
    adminListPrefetch,
    contractOf,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { optionsOf } from '@/lib/options';
import { P } from '@/lib/permissions';
import { useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';

const PATH = '/staff/cases';
const PAGE_KEY = 'cases';

/** Case priorities (the contract's `priority` filter enum). */
export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

const schema = z.object({
    title: z.string().min(1, 'A title').max(200),
    merchant_id: z.string().optional(),
    priority: z.enum(PRIORITIES),
});

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
const casesList = adminListPrefetch(PAGE_KEY, PATH);
export const casesPrefetch: Prefetch = (context) => [
    ...casesList(context),
    // the case kinds' colors, so the kind column and board paint colored
    objectColorsSpec('case_kind'),
];

const caseFilters = filterDefs(PATH, {
    q: 'Search',
    state: 'State',
    kind: 'Kind',
    priority: 'Priority',
    assigned_to: 'Assignee',
    tag: 'Tags',
    range: 'Opened',
});

export function CasesPage() {
    const navigate = useNavigate();
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<unknown>(null);
    // BM-46 §2.5: the colors staff gave case kinds (the kind column and the board's columns by kind)
    const colors = useObjectColors('case_kind');
    const form = useZodForm(schema, {
        defaultValues: { title: '', merchant_id: '', priority: 'normal' },
    });
    const create = useWrite(
        async (values: z.infer<typeof schema>) =>
            (
                await api().post<CaseRecord>('/staff/cases', {
                    title: values.title,
                    merchant_id: values.merchant_id || null,
                    priority: values.priority,
                    links: [],
                })
            ).data,
        ['/staff/cases'],
    );
    const columns: ListColumnDef<CaseRecord>[] = [
        {
            key: 'title',
            label: 'Case',
            sortKey: 'title',
            cell: (c) => (
                <Link
                    to={`/cases/${c.id}`}
                    className="hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {c.title}
                </Link>
            ),
            csv: (c) => c.title,
            locked: 'start',
            boardCard: true,
        },
        {
            key: 'kind',
            label: 'Kind',
            sortKey: 'kind',
            cell: (c) => (
                <ColorTag color={colors.colorOf('case_kind', c.kind)}>
                    {statusLabel(c.kind)}
                </ColorTag>
            ),
            csv: (c) => c.kind,
            groupable: { key: 'kind', value: (c) => c.kind },
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            cell: (c) => <StatusBadge status={c.state} domain="case" />,
            csv: (c) => c.state,
            groupable: { key: 'state', statusDomain: 'case' },
        },
        {
            key: 'priority',
            label: 'Priority',
            sortKey: 'priority',
            cell: (c) => (
                <StatusBadge
                    status={c.priority}
                    domain={ADMIN_STATUSES.priority}
                />
            ),
            csv: (c) => c.priority,
            groupable: { key: 'priority', value: (c) => c.priority },
            boardCard: true,
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            cell: (c) => (
                <span className="font-mono text-xs">
                    {c.merchant_id ?? '—'}
                </span>
            ),
            csv: (c) => c.merchant_id,
        },
        {
            // BM-87: the store of the charge the case came from; empty on manual cases and cases of no store
            key: 'store',
            label: 'Store',
            cell: (c) => (c.store ? <StoreName store={c.store} /> : '—'),
            csv: (c) => c.store?.name ?? null,
        },
        {
            key: 'assigned_to',
            aliases: ['assigned'],
            label: 'Assigned to',
            sortKey: 'assigned_to',
            cell: (c) => who(c.assigned_to, me),
            csv: (c) => c.assigned_to,
            boardCard: true,
        },
        {
            key: 'notes_count',
            aliases: ['notes'],
            label: 'Notes',
            sortKey: 'notes_count',
            cell: (c) => c.notes_count,
            csv: (c) => c.notes_count,
            noTruncate: true,
        },
        {
            key: 'updated_at',
            aliases: ['updated'],
            label: 'Updated',
            kind: 'datetime',
            sortKey: 'updated_at',
            cell: (c) => <DateTime value={c.updated_at} empty="—" />,
            csv: (c) => c.updated_at,
            noTruncate: true,
        },
        {
            key: 'tags',
            label: 'Tags',
            cell: (c) => <TagList tags={c.tags ?? []} />,
            csv: (c) => (c.tags ?? []).map((t) => t.name).join(', '),
            defaultHidden: true,
        },
    ];
    const { state, query } = useAdminList<CaseRecord>(PAGE_KEY, PATH, {
        columns,
        filters: caseFilters,
    });
    return (
        <div>
            <PageHeader
                title="Cases"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Units of staff work about a merchant or the platform.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
                primaryAction={
                    can(P.casesWrite) ? (
                        <Button onClick={() => setOpen(true)}>
                            Open a case
                        </Button>
                    ) : null
                }
                secondaryAction={
                    can(P.casesWrite) ? (
                        <KindColorsDialog
                            kinds={contractOf(PATH).filters.kind?.values ?? []}
                        />
                    ) : null
                }
            />
            <AdminListPage
                noun="cases"
                state={state}
                query={query}
                rowKey={(c) => c.id}
                rowHref={(c) => `/cases/${c.id}`}
                controls={{
                    q: { kind: 'search' },
                    state: { domain: 'case' },
                    priority: { domain: ADMIN_STATUSES.priority },
                    assigned_to: {
                        kind: 'enum',
                        options: [{ value: ME_TOKEN, label: 'Mine' }],
                    },
                    tag: <TagFilter state={state} />,
                }}
                groupColor={(key) =>
                    state.settings.groupBy === 'kind'
                        ? colors.colorOf('case_kind', key)
                        : null
                }
            />
            {/* BM-84 (rule E.9, design 0.7.0 cases): the new case in a sheet */}
            <FormSheet
                open={open}
                onOpenChange={(o) => {
                    setOpen(o);
                    if (!o) setError(null);
                }}
                size="sm"
                title="Open a manual case"
                submitLabel="Open case"
                busy={create.isPending}
                dirty={form.formState.isDirty}
                error={error}
                data-testid="case-form"
                onSubmit={form.handleSubmit((values) => {
                    setError(null);
                    create.mutate(values, {
                        onSuccess: (c) => {
                            // saved: the reset and the navigation happen in one handler, so tell the guard to let this one through
                            setOpen(false);
                            form.reset();
                            allowNextNavigation();
                            navigate(`/cases/${c.id}`);
                        },
                        onError: setError,
                    });
                })}
            >
                <Field
                    id="title"
                    label="Title"
                    error={form.formState.errors.title?.message}
                >
                    <Input id="title" {...form.register('title')} />
                </Field>
                <Field
                    id="merchant_id"
                    label="Merchant"
                    hint="Optional: the mer_ id the case is about."
                >
                    <Input
                        id="merchant_id"
                        placeholder="mer_…"
                        {...form.register('merchant_id')}
                    />
                </Field>
                <Field id="priority" label="Priority">
                    <SearchableSelect
                        id="priority"
                        label="Priority"
                        options={optionsOf(PRIORITIES)}
                        value={form.watch('priority')}
                        clearable={false}
                        onChange={(v) =>
                            v
                                ? form.setValue(
                                      'priority',
                                      v as (typeof PRIORITIES)[number],
                                  )
                                : undefined
                        }
                    />
                </Field>
            </FormSheet>
        </div>
    );
}
